/**
 * Janitorial / EVS Category Page — /marketplace/janitorial/<slug>
 * Lists all products in a single category with Add-to-Quote-Cart integration.
 */

import { useParams, Link, Navigate } from "react-router-dom";
import {
  ArrowLeft, ShoppingCart, Plus, Phone, Download,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useQuoteCart } from "@/contexts/QuoteCartContext";
import {
  JANITORIAL_CATEGORIES,
  getProductsByCategory,
  getCategoryBySlug,
} from "@/data/janitorialCatalog";
import { getProductDetails } from "@/data/janitorialCatalogDetails";
import FeaturedSpecProduct from "@/components/FeaturedSpecProduct";
import MarketplaceCategoryNav from "@/components/MarketplaceCategoryNav";
import MarketplaceBreadcrumb from "@/components/MarketplaceBreadcrumb";

export default function JanitorialCategoryPage({ basePath = "/marketplace/janitorial" }) {
  const { categorySlug } = useParams();
  const cat = getCategoryBySlug(categorySlug);
  const { addItem, setShowCart, cart } = useQuoteCart();

  if (!cat) return <Navigate to={basePath} replace />;

  const products = getProductsByCategory(categorySlug);
  const featured = products.filter(p => p.featured);
  const rest = products.filter(p => !p.featured);
  const cartCount = cart.reduce((s, i) => s + i.qty, 0);

  const handleAdd = (p) => {
    addItem({
      erp: p.sku,
      name: p.name,
      image: p.image,
      source: "janitorial",
      category: cat.name,
    });
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid={`janitorial-category-${categorySlug}`}>
      <MarketplaceCategoryNav currentCategory="janitorial-evs" />
      <MarketplaceBreadcrumb trail={[{ label: "Janitorial & EVS", to: basePath }, { label: cat.name }]} />
      {/* Top bar */}
      <header className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to={basePath}
            className="flex items-center gap-2 text-slate-300 hover:text-white"
            data-testid="back-to-janitorial-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm">All Janitorial Categories</span>
          </Link>
          <div className="flex items-center gap-3">
            <a href="tel:2814563458" className="hidden sm:flex items-center gap-1.5 text-sm text-slate-300 hover:text-white">
              <Phone className="w-4 h-4" /> 281.456.3458
            </a>
            <Button
              onClick={() => setShowCart(true)}
              size="sm"
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold relative"
              data-testid="cat-open-cart-btn"
            >
              <ShoppingCart className="w-4 h-4 mr-1.5" /> Quote Cart
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center" data-testid="cat-cart-count">
                  {cartCount}
                </span>
              )}
            </Button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-10 md:py-14 grid md:grid-cols-[1fr_auto] gap-8 items-center">
          <div>
            <p className="text-xs uppercase tracking-wider font-semibold text-emerald-700 mb-2">
              Janitorial / EVS · {products.length} {products.length === 1 ? "product" : "products"}
            </p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 mb-3 leading-[1.1]">
              {cat.name}
            </h1>
            <p className="text-slate-600 text-lg max-w-2xl">{cat.short}</p>
            <div className="mt-5 flex flex-wrap gap-3">
              {cat.pdf && (
                <a
                  href={cat.pdf.file}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-fuchsia-700 hover:text-fuchsia-800 px-3 py-1.5 rounded-md border border-fuchsia-200 hover:border-fuchsia-400 bg-fuchsia-50"
                  data-testid="cat-pdf-link"
                >
                  <Download className="w-3.5 h-3.5" /> {cat.pdf.label}
                </a>
              )}
            </div>
          </div>
          <div className="hidden md:block">
            <div className="w-48 h-48 rounded-2xl overflow-hidden border border-slate-200 bg-slate-50">
              <img
                src={cat.hero}
                alt={cat.name}
                className="w-full h-full object-contain p-3"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* PRODUCTS GRID */}
      <section className="max-w-7xl mx-auto px-6 py-12">
        {featured.map((p) => (
          <FeaturedSpecProduct
            key={p.sku}
            product={{ ...p, galleryImages: getProductDetails(p.sku)?.images }}
            detailsTo={`${basePath}/${categorySlug}/${p.sku}`}
            onAdd={() => handleAdd(p)}
            onAddAndView={() => { handleAdd(p); setShowCart(true); }}
          />
        ))}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-slate-900">Products in {cat.name}</h2>
          <Button
            variant="outline"
            onClick={() => {
              products.forEach(p => handleAdd(p));
              setShowCart(true);
            }}
            data-testid="cat-add-all-btn"
            className="text-sm border-emerald-200 text-emerald-700 hover:bg-emerald-50"
          >
            <ShoppingCart className="w-4 h-4 mr-1.5" /> Add all to quote
          </Button>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {rest.map((p) => (
            <div
              key={p.sku}
              className="group bg-white rounded-xl overflow-hidden border border-slate-200 hover:border-emerald-400 hover:shadow-lg transition-all flex flex-col"
              data-testid={`product-card-${p.sku}`}
            >
              <Link
                to={`${basePath}/${categorySlug}/${p.sku}`}
                className="aspect-square bg-white border-b border-slate-100 overflow-hidden block"
                data-testid={`product-link-${p.sku}`}
              >
                <img
                  src={p.image}
                  alt={p.name}
                  className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform"
                  loading="lazy"
                  onError={(e) => { e.currentTarget.src = `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'><rect width='200' height='200' fill='%23f1f5f9'/><text x='100' y='105' text-anchor='middle' fill='%2394a3b8' font-family='sans-serif' font-size='12'>${encodeURIComponent(p.name)}</text></svg>`; }}
                />
              </Link>
              <div className="p-4 flex flex-col flex-1">
                <Badge className="self-start bg-slate-100 text-slate-600 text-[10px] uppercase font-semibold mb-2">
                  {cat.name}
                </Badge>
                <Link
                  to={`${basePath}/${categorySlug}/${p.sku}`}
                  className="font-semibold text-slate-900 hover:text-emerald-700 text-sm leading-snug mb-1 line-clamp-2 min-h-[2.5em]"
                >
                  {p.name}
                </Link>
                <p className="text-xs text-slate-500 mb-3">SKU {p.sku}</p>
                {p.description && (
                  <p className="text-xs text-slate-600 leading-relaxed mb-3 line-clamp-3">
                    {p.description}
                  </p>
                )}
                <div className="mt-auto flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleAdd(p)}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold"
                    data-testid={`add-to-cart-${p.sku}`}
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" /> Quick Add
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => { handleAdd(p); setShowCart(true); }}
                    variant="outline"
                    className="text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                    data-testid={`add-and-view-${p.sku}`}
                  >
                    <ShoppingCart className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <Link
                  to={`${basePath}/${categorySlug}/${p.sku}`}
                  className="mt-2.5 text-[11px] text-emerald-700 hover:text-emerald-800 font-medium inline-flex items-center gap-1"
                  data-testid={`view-details-${p.sku}`}
                >
                  View full details &amp; downloads →
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* OTHER CATEGORIES */}
      <section className="max-w-7xl mx-auto px-6 pb-16">
        <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-4">More Janitorial categories</h2>
        <div className="flex flex-wrap gap-2">
          {JANITORIAL_CATEGORIES.filter(c => c.slug !== categorySlug).map(c => (
            <Link
              key={c.slug}
              to={`${basePath}/${c.slug}`}
              className="px-3 py-1.5 rounded-full bg-white border border-slate-200 text-sm text-slate-700 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
              data-testid={`other-cat-${c.slug}`}
            >
              {c.name}
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
